import { Fragment, useMemo, useState, useCallback, MouseEvent } from 'react'
import moment from 'moment-timezone'
import { isEmpty, map, prop } from 'ramda'
import { useTranslation } from 'react-i18next'
import cn from 'classnames/bind'

import {
  Table,
  Tr,
  TdLabel,
  Th,
  TrSelectable,
  useCopyColumnsWidths,
} from 'app/common/ui-components/table'

import toFixedValue, { makeCnbyValue } from 'app/common/lib/toFixedValue'
import { useMonthDaysAsMoment } from 'app/common/hooks/useMonthDays'
import { BannerContent } from 'app/common/components-next/banner'
import EmptyWarn from 'app/common/layouts/EmptyWarn'
import Loading from 'app/common/components/Loader'
import { useCurrencySign } from 'app/common/components-next/currency'
import { PlainSideSlider } from 'app/common/ui-next/plain-side-slider'
import Sticky from 'app/common/templates-next/sticky'
import { PageTemplateContent } from 'app/common/templates-next/page-template'

import ItemDetails from './ItemDetails'
import styles from './FinanceHistoryTable.module.scss';

const cx = cn.bind(styles)
const cnbyValue = makeCnbyValue(styles.negative, styles.positive, '')
const cnbyValueSum = makeCnbyValue(styles.negative, '', '')
const mToday = moment()

type Props = {
  timezone: string
  date: string // YYYY-MM
  bannerPlaceId?: string
  data: object // TODO
  loading: boolean
}

const FinanceHistoryTable = ({ date, timezone, bannerPlaceId, data, loading }: Props) => {
  const { t } = useTranslation('finances')
  if (!timezone) {
    console.error("FinanceHistoryTable — не пришел обязательный параметр 'timezone'") // eslint-disable-line no-console
  }

  const mDays = useMonthDaysAsMoment(date)
  const byDay = useMemo(() => prop('details', data) || {}, [data])

  const items = useMemo(() => {
    if (isEmpty(byDay)) return []

    return map((mDay) => {
      const dateKey = mDay.format('YYYY-MM-DD')
      const item = byDay[dateKey] || {}
      const isToday = mToday.isSame(mDay, 'day')
      return {
        dateKey,
        day: mDay.format('DD.MM'),
        dayStartSum: toFixedValue(item.at_start),
        dayEndSum: !item.at_end && isToday ? '—' : toFixedValue(item.at_end),
        added: item.added ? `${toFixedValue(item.added)}` : '',
        subtracted: item.subtracted ? `−${toFixedValue(Math.abs(item.subtracted))}` : '',
      }
    }, mDays)
  }, [byDay, mDays])

  const emptyData = isEmpty(items)

  const [refFrom, refTo] = useCopyColumnsWidths()

  const [details, setDetails] = useState('') // dateKey
  const handleChangeDetails = useCallback(
    (id: string, checked: boolean) => {
      if (details === id) {
        setDetails(checked ? id : '')
        return
      }
      setDetails(id)
    },
    [details],
  )
  const handleCloseDetails = useCallback((e: MouseEvent<HTMLElement>) => {
    e.preventDefault()
    setDetails('')
  }, [])

  const currency = useCurrencySign('commaSpaceBefore')

  return (
    <Fragment>
      <Sticky>
        {(ref, sticky, shadow) => (
          <PageTemplateContent
            className={cx({ sticky })}
            reference={ref}
            shadowBottom={shadow}
            skipPadding
            flex
          >
            <div className={styles.tableContainer}>
              <Table className={styles.table}>
                <thead>
                  <Tr header reference={refTo} headerHasBorderBottom={false}>
                    <Th alignLeft id="header1" className={styles.th}>
                      {t('column_title_data')}
                    </Th>
                    <Th alignRight id="header2" className={styles.th}>
                      {t('column_title_balance_begin')}
                      {currency}
                    </Th>
                    <Th alignRight id="header3" className={styles.th}>
                      {t('column_title_refill')}
                      {currency}
                    </Th>
                    <Th alignRight id="header4" className={styles.th}>
                      {t('column_title_write_off')}
                      {currency}
                    </Th>
                    <Th alignRight id="header5" className={styles.th}>
                      {t('column_title_balance_end')}
                      {currency}
                    </Th>
                  </Tr>
                </thead>
              </Table>
            </div>

            {bannerPlaceId && (
              <BannerContent
                placeId="d099e4c6-f44b-42ef-b5f9-a1d52c27dee5"
                className={styles.bannerPlaceholder}
                displayAsPlaceholder
                height={0}
              />
            )}
          </PageTemplateContent>
        )}
      </Sticky>

      <PageTemplateContent className={styles.layout} skipPadding flex>
        <div className={styles.tableContainer}>
          {emptyData && !loading && <EmptyWarn>{t('empty')}</EmptyWarn>}
          {loading && <Loading />}

          {!emptyData && !loading && (
            <Table className={styles.table}>
              <thead>
                <Tr header hidden reference={refFrom}>
                  <Th alignLeft className={styles.th}>
                    {t('column_title_data')}
                  </Th>
                  <Th alignRight className={styles.th}>
                    {t('column_title_balance_begin')}
                  </Th>
                  <Th alignRight className={styles.th}>
                    {t('column_title_refill')}
                  </Th>
                  <Th alignRight className={styles.th}>
                    {t('column_title_write_off')}
                  </Th>
                  <Th alignRight className={styles.th}>
                    {t('column_title_balance_end')}
                  </Th>
                </Tr>
              </thead>
              <tbody>
                {!emptyData &&
                  items.map(({ dateKey, day, dayStartSum, added, subtracted, dayEndSum }) => (
                    <TrSelectable
                      key={day}
                      id={dateKey}
                      onClick={handleChangeDetails}
                      selected={details ? dateKey === details : false}
                      classNameFocused={styles.focusedRow}
                      loading={loading}
                    >
                      <TdLabel htmlFor={dateKey} headers="header1" className={styles.positive}>
                        {day}
                      </TdLabel>
                      <TdLabel
                        htmlFor={dateKey}
                        headers="header2"
                        alignRight
                        className={cnbyValueSum(dayStartSum)}
                      >
                        {dayStartSum}
                      </TdLabel>
                      <TdLabel
                        htmlFor={dateKey}
                        headers="header3"
                        alignRight
                        className={cnbyValue(added)}
                      >
                        {added}
                      </TdLabel>
                      <TdLabel
                        htmlFor={dateKey}
                        headers="header4"
                        alignRight
                        className={cnbyValue(subtracted)}
                      >
                        {subtracted}
                      </TdLabel>
                      <TdLabel
                        htmlFor={dateKey}
                        headers="header5"
                        alignRight
                        className={cnbyValueSum(dayEndSum)}
                        last
                      >
                        {dayEndSum}
                      </TdLabel>
                    </TrSelectable>
                  ))}
              </tbody>
            </Table>
          )}
        </div>

        {bannerPlaceId && (
          <BannerContent
            // leaveContainerOnEmpty
            refreshOnMount
            className={styles.bannerContent}
            placeId="d099e4c6-f44b-42ef-b5f9-a1d52c27dee5"
          />
        )}
      </PageTemplateContent>

      <PlainSideSlider
        open={!!details}
        onCrossClick={handleCloseDetails}
        position={{
          top: 0,
          right: 0,
          bottom: 0,
        }}
      >
        <ItemDetails timezone={timezone} date={details} details={byDay[details] || []} />
      </PlainSideSlider>
    </Fragment>
  )
}

export default FinanceHistoryTable
